import {useState} from 'react';

import {BaseCondition} from 'studies/typed/shared/useExperimentDesignState';

import {buildConditionsFromTableUpdate} from './replicateTableUtils';

interface UseConditionDescriptionDraftParams {
  conditions: Array<BaseCondition>;
  setConditions: (conditions: Array<BaseCondition>, prevIndexes: Array<number | null>) => void;
  isPlan: boolean;
}

interface UseConditionDescriptionDraftResult {
  isEditDescriptionModalOpen: boolean;
  descriptionDraft: string;
  setDescriptionDraft: (descriptionDraft: string) => void;
  openEditDescriptionModal: (conditionIndex: number) => void;
  closeEditDescriptionModal: () => void;
  saveDescriptionDraft: () => void;
}

export function useConditionDescriptionDraft({
  conditions,
  setConditions,
  isPlan,
}: UseConditionDescriptionDraftParams): UseConditionDescriptionDraftResult {
  const [isEditDescriptionModalOpen, setIsEditDescriptionModalOpen] = useState(false);
  const [descriptionConditionIndex, setDescriptionConditionIndex] = useState<number | null>(null);
  const [descriptionDraft, setDescriptionDraft] = useState('');

  const openEditDescriptionModal = (conditionIndex: number): void => {
    setDescriptionConditionIndex(conditionIndex);
    setDescriptionDraft(conditions[conditionIndex]?.description ?? '');
    setIsEditDescriptionModalOpen(true);
  };

  const closeEditDescriptionModal = (): void => {
    setIsEditDescriptionModalOpen(false);
    setDescriptionConditionIndex(null);
    setDescriptionDraft('');
  };

  const saveDescriptionDraft = (): void => {
    if (descriptionConditionIndex == null) {
      return;
    }
    const newConditions = conditions.map((condition, index) => ({
      name: condition.name,
      numReplicates: condition.numReplicates,
      description: index === descriptionConditionIndex ? descriptionDraft : condition.description,
    }));
    const originalValueIndexes = conditions.map((_condition, index) => index);
    setConditions(
      buildConditionsFromTableUpdate({
        newConditions,
        originalValueIndexes,
        conditions,
        isPlan,
      }),
      originalValueIndexes
    );
    closeEditDescriptionModal();
  };

  return {
    isEditDescriptionModalOpen,
    descriptionDraft,
    setDescriptionDraft,
    openEditDescriptionModal,
    closeEditDescriptionModal,
    saveDescriptionDraft,
  };
}
